import { IconCopy } from "@tabler/icons-react";
import { useCallback, useEffect, useRef, useState } from "react";

const FEEDBACK_MS = 1_600;

interface CopyBlockProps {
  /** Text displayed and copied to the clipboard. */
  value: string;
  /** Accessible name of the button, e.g. "Copy the two lines". */
  label: string;
  /** Render the text over several lines. */
  multiline?: boolean;
}

/**
 * A monospace block that copies its own content when clicked.
 *
 * Args:
 *     value: Text to display and copy.
 *     label: Accessible name for the button.
 *     multiline: Keep line breaks instead of truncating to one line.
 *
 * Returns:
 *     A clickable code block with transient "copied" feedback.
 */
export function CopyBlock({
  value,
  label,
  multiline = false,
}: CopyBlockProps): JSX.Element {
  const [copied, setCopied] = useState(false);
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const copy = useCallback(async (): Promise<void> => {
    try {
      await navigator.clipboard?.writeText(value);
      setCopied(true);
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setCopied(false), FEEDBACK_MS);
    } catch {
      // Clipboard denied or unavailable: the text stays selectable on screen.
    }
  }, [value]);

  return (
    <button
      type="button"
      className={`code-block${multiline ? " code-block--multiline" : ""}`}
      onClick={() => void copy()}
      aria-label={label}
      title={label}
    >
      <span className="code-block__text">{value}</span>
      <span className="code-block__action" aria-hidden="true">
        {copied ? "copied" : <IconCopy size={13} />}
      </span>
      <span className="sr-only" role="status">
        {copied ? "Copied to the clipboard" : ""}
      </span>
    </button>
  );
}
